import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { resolveCtaUrl } from "../../lib/cta";
import { useCurrency } from "../../context/CurrencyContext";

export default function Pricing({ data, content }) {
  const { currency } = useCurrency();
  const plans = data.plans || [];

  const pickPrice = (plan) => {
    if (currency === "EU" && plan.priceEU) return plan.priceEU;
    return plan.price;
  };
  const pickPeriod = (plan) => {
    if (currency === "EU" && plan.periodEU) return plan.periodEU;
    return plan.period;
  };
  const pickOriginal = (plan) => {
    if (currency === "EU" && plan.originalPriceEU) return plan.originalPriceEU;
    return plan.originalPrice;
  };

  return (
    <section data-testid="pricing-section" className="py-24 lg:py-32 bg-alabaster">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="overline mb-4" data-testid="pricing-overline">{data.overline}</p>
          <h2 data-testid="pricing-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-onyx">
            {data.title}
          </h2>
          <p className="mt-4 text-onyx/70">{data.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, i) => {
            const isHl = plan.highlighted;
            const href = resolveCtaUrl(content, plan.ctaUrl);
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`pricing-plan-${i}`}
                className={`relative p-8 lg:p-10 border ${
                  isHl
                    ? "bg-onyx text-alabaster border-onyx scale-100 md:scale-105 shadow-2xl"
                    : "bg-white text-onyx border-border"
                }`}
              >
                {plan.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] tracking-[0.25em] uppercase font-bold bg-gold text-onyx">
                    {plan.badge}
                  </span>
                )}
                <h3 className={`font-serif text-3xl ${isHl ? "text-alabaster" : "text-onyx"}`}>{plan.name}</h3>
                {plan.description && (
                  <p className={`mt-2 text-sm ${isHl ? "text-alabaster/60" : "text-onyx/60"}`}>
                    {plan.description}
                  </p>
                )}
                <div className="mt-6 flex items-baseline gap-1">
                  <span className={`font-serif text-5xl ${isHl ? "text-gold" : "text-onyx"}`}>{pickPrice(plan)}</span>
                  {pickPeriod(plan) && <span className={`text-sm ${isHl ? "text-alabaster/60" : "text-onyx/60"}`}>{pickPeriod(plan)}</span>}
                </div>
                {pickOriginal(plan) && (
                  <p className={`mt-1 text-xs line-through ${isHl ? "text-alabaster/40" : "text-onyx/40"}`}>
                    De {pickOriginal(plan)}
                  </p>
                )}
                <ul className="mt-6 space-y-3">
                  {(plan.features || []).map((f, k) => (
                    <li key={k} className={`flex items-start gap-3 text-sm ${isHl ? "text-alabaster/90" : "text-onyx/80"}`}>
                      <Check size={14} className={`mt-1 flex-shrink-0 ${isHl ? "text-gold" : "text-gold"}`} strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`pricing-cta-${i}`}
                  className={`mt-8 group w-full inline-flex items-center justify-center gap-3 py-3.5 text-xs tracking-[0.2em] uppercase font-semibold transition-colors ${
                    isHl
                      ? "bg-gold text-onyx hover:bg-alabaster"
                      : "bg-onyx text-alabaster hover:bg-gold hover:text-onyx"
                  }`}
                >
                  {plan.ctaLabel}
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </a>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
